import { useMemo } from "react";
import type { BrandFontFamilySlot } from "@/hooks/use-kit";

// Quote a string for safe use as a CSS <string> value (e.g. font-family).
// Escapes backslashes and double-quotes so a name containing `"` can't
// break out of the CSS string and inject arbitrary declarations.
export function cssQuote(s: string): string {
  return `"${s.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
}

function formatHint(url: string): string {
  const u = url.toLowerCase();
  if (u.endsWith(".woff2")) return "woff2";
  if (u.endsWith(".woff")) return "woff";
  if (u.endsWith(".ttf")) return "truetype";
  if (u.endsWith(".otf")) return "opentype";
  return "woff2";
}

interface Props {
  families: (BrandFontFamilySlot | null)[];
}

export function BrandFontFaceInjector({ families }: Props) {
  const css = useMemo(() => {
    const blocks: string[] = [];
    const seen = new Set<string>();
    for (const fam of families) {
      if (!fam || fam.source !== "CUSTOM") continue;
      if (seen.has(fam.id)) continue;
      seen.add(fam.id);
      for (const f of fam.files) {
        blocks.push(
          `@font-face { font-family: ${cssQuote(fam.name)}; src: url(${cssQuote(f.url)}) format(${cssQuote(formatHint(f.url))}); font-weight: ${f.fontWeight ?? 400}; font-style: ${f.fontStyle ?? "normal"}; font-display: swap; }`,
        );
      }
    }
    return blocks.join("\n");
  }, [families]);

  if (!css) return null;
  return <style>{css}</style>;
}
